import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getAllNoticePosts } from '../api/firebase';

const NoticePostsContext = createContext();

// 캐시 만료 시간 (5분)
const CACHE_EXPIRY_TIME = 5 * 60 * 1000;

// localStorage 키
const CACHE_KEY = 'noticePostsCache';
const CACHE_TIMESTAMP_KEY = 'noticePostsCacheTimestamp';

export const NoticePostsProvider = ({ children }) => {
  const [posts, setPosts] = useState({
    events: [],
    update_and_notice: [],
    FAQ: []
  });
  const [isLoading, setIsLoading] = useState(false);
  const [lastFetchTime, setLastFetchTime] = useState(null);
  const [error, setError] = useState(null);

  // localStorage에서 캐시 로드
  const loadFromCache = useCallback(() => {
    try {
      const cachedData = localStorage.getItem(CACHE_KEY);
      const cachedTimestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY);

      if (cachedData && cachedTimestamp) {
        const timestamp = parseInt(cachedTimestamp, 10);
        const now = Date.now();

        // 캐시가 유효한지 확인 (5분 이내)
        if (now - timestamp < CACHE_EXPIRY_TIME) {
          const parsedData = JSON.parse(cachedData);
          console.log('NoticePostsContext: 캐시에서 데이터 로드', parsedData);
          setPosts(parsedData);
          setLastFetchTime(timestamp);
          return true;
        } else {
          console.log('NoticePostsContext: 캐시 만료됨');
          // 만료된 캐시 삭제
          localStorage.removeItem(CACHE_KEY);
          localStorage.removeItem(CACHE_TIMESTAMP_KEY);
        }
      }
    } catch (error) {
      console.error('캐시 로드 오류:', error);
    }
    return false;
  }, []);

  // localStorage에 캐시 저장
  const saveToCache = useCallback((data) => {
    try {
      const timestamp = Date.now();
      localStorage.setItem(CACHE_KEY, JSON.stringify(data));
      localStorage.setItem(CACHE_TIMESTAMP_KEY, timestamp.toString());
      setLastFetchTime(timestamp);
      console.log('NoticePostsContext: 캐시에 데이터 저장', data);
    } catch (error) {
      console.error('캐시 저장 오류:', error);
    }
  }, []);

  // Firestore에서 게시물 불러오기
  const fetchPosts = useCallback(async (forceRefresh = false) => {
    // 강제 새로고침이 아니고, 캐시가 유효하면 캐시 사용
    if (!forceRefresh && loadFromCache()) {
      console.log('NoticePostsContext: 유효한 캐시 사용, Firestore 요청 생략');
      return { success: true, fromCache: true };
    }

    setIsLoading(true);
    setError(null);

    try {
      console.log('NoticePostsContext: Firestore에서 게시물 불러오는 중...');
      const result = await getAllNoticePosts();

      if (result.success) {
        console.log('NoticePostsContext: Firestore에서 게시물 로드 성공', result.data);
        setPosts(result.data);
        saveToCache(result.data);
        return { success: true, fromCache: false };
      } else {
        setError(result.error || '게시물 로드 실패');
        return { success: false, error: result.error };
      }
    } catch (err) {
      console.error('게시물 로드 오류:', err);
      setError('게시물을 불러오는 중 오류가 발생했습니다.');
      return { success: false, error: err.message };
    } finally {
      setIsLoading(false);
    }
  }, [loadFromCache, saveToCache]);

  // 캐시 무효화 (관리자가 게시물 수정 시 호출)
  const invalidateCache = useCallback(() => {
    console.log('NoticePostsContext: 캐시 무효화');
    localStorage.removeItem(CACHE_KEY);
    localStorage.removeItem(CACHE_TIMESTAMP_KEY);
    setLastFetchTime(null);
  }, []);

  // 컴포넌트 마운트 시 캐시에서 로드 시도
  useEffect(() => {
    loadFromCache();
  }, [loadFromCache]);

  const value = {
    posts,
    isLoading,
    error,
    lastFetchTime,
    fetchPosts,
    invalidateCache
  };

  return (
    <NoticePostsContext.Provider value={value}>
      {children}
    </NoticePostsContext.Provider>
  );
};

// Context 사용을 위한 커스텀 훅
export const useNoticePosts = () => {
  const context = useContext(NoticePostsContext);
  if (!context) {
    throw new Error('useNoticePosts는 NoticePostsProvider 내부에서 사용되어야 합니다.');
  }
  return context;
};

export default NoticePostsContext;

